import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Square, Download, Bot, Brain, MessageSquareWarning, ArrowDown, ChevronDown, LogOut, Plus, X, FileText, Paperclip, Image as ImageIcon, Loader2, RotateCcw, Copy, Check, BarChart3, Terminal, GraduationCap, ChevronRight } from "lucide-react";

import ActionBar from "./ActionBar";
import CallContextCard from "./CallContextCard";
import ResultsPanel from "./ResultsPanel";
import SessionContextDrawer from "./SessionContextDrawer";
import TurnBreakdownPanel from "./TurnBreakdownPanel";
import StreamingMessage from "./StreamingMessage";
import SourceCards from "./SourceCards";
import QuestionCard, { FOLLOWUPS_MARKER } from "./QuestionCard";
import { AnsweredBatchCard, AnsweredBatchSummary, useBatchPicks } from "./QuestionBatch";
import { buildPartialAnswers, isBatchQuestion } from "./utils/batchAnswer";
import UserProfileMenu from "../UserProfileMenu";
import ImagePreviewModal from "../ImagePreviewModal";
import CaseAnalysisPanels from "../CaseAnalysisPanels";
import EndChatConfirmModal from "./EndChatConfirmModal";
import JSZip from "jszip";
import { openDocument } from "./utils/openDocument";
import { authFetch } from "../../utils/auth";
import { FEATURE_SESSION_STATS, useTenantFeature } from "../../tenant/tenantConfig";
import { formatTimestamp, citedSourceNumbers, formatBotMessage, stripManifest } from "./utils/formatMessage";
import { ACCEPT_IMAGES, ACCEPT_DOCUMENTS, MAX_ATTACHMENTS, SUPPORTED_FORMATS_LABEL, IMAGE_FORMATS_LABEL, DOCUMENT_STABLE_LABEL, DOCUMENT_BETA_LABEL, isBetaExt, extOf, triageFiles, formatBytes } from "./utils/attachments";
import { isAnalyzable, stageForAnalyzer } from "./utils/analyzerHandoff";
import { getTrailBySession } from "../learning/useLearning";
import { RETRY_DELAYS_MS } from "../learning/LearningMode";
import DiagnosisPromptBanner from "../brain/DiagnosisPromptBanner";
import GuidedOfferRow, { OFFER_STATES } from "../brain/GuidedOfferRow";
import LearningPromptRow from "../brain/LearningPromptRow";

// The server appends this exact option to the suggestions when the pool has
// a flowchart to walk (tse_session._STEP_BY_STEP_LABEL). Kept in sync by
// string, which is why it is one constant and not an inline literal.
const STEP_BY_STEP_LABEL = "Show step by step guidance";

/**
 * Which cards the answer actually SELECTED.
 *
 * The server decides this from the source manifest and ships it as `cited` on every
 * card, so use that. Re-deriving it from the [n] pills in the prose does not work: a
 * pill written to DISMISS a source ("[2] is a case handoff runbook with no pricing
 * data") is indistinguishable from one written to rely on it, so an answer that ruled
 * every document out lit a green tick on all five while the server had correctly
 * marked none of them selected.
 *
 * Falls back to the prose scan for payloads that carry no `cited` field, so paths
 * which do not send it keep today's behaviour rather than losing their ticks.
 */
const citedNumbersFor = (msg) => {
  const sources = msg.sources || [];
  // Union: start with prose [Sn] pills, then add manifest-marked cards
  const cited = citedSourceNumbers(msg.text, sources);
  sources.forEach((s, i) => {
    if (s.cited) cited.add(s.n || i + 1);
  });
  return cited;
};

// Turn count, cost and the hover breakdown are internal-only: gated on the
// tenant's `session_stats` feature flag (TENANT_FEATURES, see
// src/tenant/tenantConfig.js) — the same flag Dashboard2 and BatchMode read —
// so customer-facing deployments never see per-turn cost or latency. Dev gets
// it the same way, from server/.env.

// Matches Dashboard2's composer: one line at rest, grows to 200px, snaps back
// after send. Must equal the input's line-height (see .d5-composer-input) —
// autoResize sizes from scrollHeight, so a taller floor than one line makes the
// box snap back to a height the text does not fill and the placeholder rides
// high in it.
const COMPOSER_MIN_HEIGHT = "20px";
// DA batch card (owner 23 Oct): the composer's send button is the batch's only Send.
const BATCH_PENDING_PLACEHOLDER = "Answer the questions above, then send";
const BATCH_READY_PLACEHOLDER = "Press Enter to send your answers";
const BATCH_SEND_LABEL = "Send answers";
const COMPOSER_MAX_HEIGHT = 200;

// How close to the bottom counts as "caught up". One constant on purpose: it is
// both when the jump-to-bottom button hides and when autoscroll resumes
// following, and those must be the same line or the button can sit there
// offering a jump the panel has already made.
const SCROLL_BOTTOM_SLACK = 80;

// Breathing room left above the anchored question when autoscroll parks it at
// the top. Scrolling it to exactly 0 scrolls the scroller's own padding away
// too, so the bubble ends up jammed against the action bar and reads as
// clipped. Normally the scroller's padding-top (14px, less at the narrow
// breakpoints), floored so the gap never collapses on a small window.
const ANCHOR_TOP_GAP_MIN = 12;
const anchorTopGap = (body) => {
  const pad = parseFloat(getComputedStyle(body).paddingTop);
  return Math.max(Number.isFinite(pad) ? pad : 0, ANCHOR_TOP_GAP_MIN);
};

// The element that renders the engineer's own bubble for a message id. It is
// deliberately the BUBBLE (.d5-msg-user), not its .d5-msg-block wrapper: on a
// guided turn that wrapper also holds the copilot's question card above the
// reply, so anchoring on it parks the card at the top and leaves the engineer's
// own line ~230px down the pane. Scanned rather than selector-matched: message
// ids come from the server and are not guaranteed to be CSS-escapable.
const findEngineerBlock = (body, id) => {
  if (!body || id == null) return null;
  const wanted = String(id);
  const blocks = body.querySelectorAll(".d5-engineer-block");
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    if (blocks[i].getAttribute("data-msg-id") === wanted) return blocks[i];
  }
  return null;
};

// Result rows (bugs / cases) as the panel shows them — heading, status, match, link.
const resultLines = (heading, items = [], meta = {}) => {
  if (!items.length) return [];
  const out = ["", `## ${heading}`, ""];
  if (meta.lowRelevance) out.push("_Low relevance — no strong match found._", "");
  if (meta.sectionText) out.push(meta.sectionText, "");
  items.forEach((item) => {
    const id = item.bug_id || item.case_number || item.case_id || item.id || item.title || "Unknown";
    const pct = Number(item.match_pct ?? item.score) || 0;
    out.push(`### ${id}${pct ? ` — ${Math.round(pct)}% match` : ""}`);
    if (item.title && item.title !== id) out.push(`**${item.title}**`);
    if (item.status) out.push(`Status: ${item.status}`);
    const body = item.description || item.summary || item.subject || "";
    if (body) out.push("", body);
    if (item.url) out.push("", item.url);
    out.push("");
  });
  return out;
};

// The diagnostic map as text: which graphs were loaded, and the path walked through
// them. The canvas itself cannot go in a markdown file, but this is the part that
// carries the reasoning — without it the export cannot show HOW the answer was reached.
const diagLines = (snapshot) => {
  if (!snapshot) return [];
  const runbooks = snapshot.selected_runbooks || [];
  const path = snapshot.traversal_path || snapshot.visited_nodes || [];
  if (!runbooks.length && !path.length) return [];
  const nodes = Object.fromEntries(
    ((snapshot.diagnostic_subgraph || {}).nodes || []).map((n) => [n.nid, n])
  );
  const out = ["", "## Diagnostic map", ""];
  if (runbooks.length) {
    out.push("**Graphs selected**", "");
    runbooks.forEach((name) => out.push(`- ${name}`));
    out.push("");
  }
  if (path.length) {
    out.push("**Path traversed**", "");
    path.forEach((nid, i) => {
      const node = nodes[nid] || {};
      out.push(`${i + 1}. ${node.name || nid}${node.type ? ` _(${node.type})_` : ""}`);
      if (node.description && node.description !== node.name) {
        out.push(`   - ${node.description}`);
      }
    });
    out.push("");
  }
  return out;
};

const buildSessionMarkdown = (messages, sessionStats, extras = {}) => {
  const {
    bugs = [], cases = [], resultsMeta = {},
    currentQuestion = null, diagSnapshot = null, sessionId = "",
  } = extras;
  const lines = ["# Copilot Session", ""];
  // Session id in the document itself, not just the filename — a pasted excerpt has
  // to be traceable back to the server-side log for the same session.
  if (sessionId) lines.push(`**Session ID:** \`${sessionId}\``, "");
  if (sessionStats?.turns > 0) {
    let summary = `**Turns:** ${sessionStats.turns}`;
    if (sessionStats.totalCostUsd > 0) summary += ` · **Total cost:** $${sessionStats.totalCostUsd.toFixed(4)}`;
    lines.push(summary, "", "---");
  }
  for (const msg of messages) {
    // Transient "Searching…" rows are UI chrome, not content, so they stay out.
    if (msg.type === "status") continue;
    // The learning switch note explains why the next turn went the way it did, so it belongs in the record.
    if (msg.type === "learning_note") {
      lines.push("", `> **Learning ${msg.enabled ? "on" : "off"}** — ${msg.text}`);
      continue;
    }
    if (msg.type === "session_clear_divider") {
      lines.push("", "---", "", "### — New session —", "", "---");
      continue;
    }
    const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    const label = msg.sender === "live_agent"
      ? (msg.agentName || "Support Agent")
      : (msg.sender === "agent" || msg.sender === "user" ? "You" : "Copilot");
    // Stripped exactly like the on-screen render: the manifest is machine data
    // for the source cards, and it was landing in the exported file as raw
    // "::sources / S1 | why / ::end" rows under the answer.
    const body = stripManifest(msg.type === "case_analysis"
      ? (msg.analysis?.case_analysis || "[Case Analysis]")
      : (msg.text || ""));
    lines.push("", `## ${label}${ts ? ` — ${ts}` : ""}`, "");
    // Sources ABOVE the answer, mirroring the UI — SourceCards render before the
    // message bubble, so the export reads in the order the engineer saw it. They must
    // travel with the answer either way: the [S1] pills are meaningless without them,
    // which is what made the old export useless for tracing where an answer came from.
    const sources = msg.sources || [];
    if (sources.length) {
      lines.push("**Sources**", "");
      sources.forEach((src, i) => {
        const n = src.n || i + 1;
        const link = src.citation || src.url || "";
        lines.push(`- **[S${n}]** ${src.title || "Untitled"}${link ? ` — ${link}` : ""}`);
        const why = src.whySelected || src.why_selected;
        if (why) lines.push(`  - _Why selected:_ ${why}`);
      });
      lines.push("");
    }
    lines.push(body, "", "---");
  }

  // Anything still on screen when the export was taken: the open question card, and
  // the bugs/cases panels, which are part of the answer surface rather than the chat.
  if (currentQuestion?.question) {
    lines.push("", "## Open question", "", currentQuestion.question, "");
    (currentQuestion.options || []).forEach((opt, i) => {
      lines.push(`${i + 1}. ${typeof opt === "string" ? opt : opt?.label ?? String(opt)}`);
    });
    lines.push("", "---");
  }
  lines.push(...diagLines(diagSnapshot));
  lines.push(...resultLines("Related bugs", bugs, resultsMeta.bugs || {}));
  lines.push(...resultLines("Related cases", cases, resultsMeta.cases || {}));
  return lines.join("\n");
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export the session as a zip: the markdown record of what was on screen, plus the
 * server-side diagnostic log holding the internal detail (node visits, reasoning,
 * per-call cost) that the markdown deliberately leaves out.
 *
 * The log is best-effort — it only exists for a live socket session, so an expired or
 * HTTP-only session still exports the markdown rather than failing the whole download.
 */
const downloadSessionBundle = async (messages, sessionStats, extras = {}) => {
  const { sessionId = "" } = extras;
  const markdown = buildSessionMarkdown(messages, sessionStats, extras);
  const stamp = new Date().toISOString().slice(0, 10);
  const base = sessionId ? `session-${sessionId}` : `copilot-session-${stamp}`;

  let log = null;
  if (sessionId) {
    try {
      const res = await authFetch(`/api/tse/session-log/${encodeURIComponent(sessionId)}`);
      if (res.ok) {
        log = {
          // Named after the file that actually exists on the server, so the zip entry
          // can be matched back to it; falls back to the session id.
          name: res.headers.get("X-Log-Filename") || `${base}.log`,
          text: await res.text(),
        };
      }
    } catch {
      log = null;   // network/auth failure must not cost the user their transcript
    }
  }

  if (!log) {
    saveBlob(new Blob([markdown], { type: "text/markdown;charset=utf-8" }), `${base}.md`);
    return;
  }
  const zip = new JSZip();
  zip.file(`${base}.md`, markdown);
  zip.file(log.name, log.text);
  saveBlob(await zip.generateAsync({ type: "blob" }), `${base}.zip`);
};

/**
 * CopilotPanel — right-side copilot chat: header, action bar, message list,
 * question card, and composer. All state lives in the parent (TseDashboard5);
 * this component is presentational plus local composer input state.
 */
const capName = (name) => {
  if (!name) return "";
  const s = String(name).trim();
  if (s.length > 10) {
    return s.slice(0, 10) + "…";
  }
  return s;
};

const CopilotPanel = forwardRef(({
  messages = [],
  currentQuestion,
  isStreaming = false,
  // V5 DA narration announced (tse:da_narration_pending) but not started: same thinking row as a turn.
  narrationPending = false,
  sessionStats = { turns: 0, totalCostUsd: 0 },
  onSend,
  onAction,
  onCaseAnalysisSubmit,
  onAnswer,
  onDismissQuestion,
  onStop,
  onShareToCustomer,
  onEditInCustomer,
  onOpenFeedback,
  onResumeContextSwitch,
  feedbackNotice = "",
  actionNotice = null,
  variant = "default",
  agentNameOverride = "",
  sentiment,
  prevSentiment,
  onLogout,
  onDocError,
  onOpenSourceMap,
  onReplaceSource,
  onCiteSource,
  bugs = [],
  cases = [],
  resultsMeta = {},
  escalationActive = false,
  liveChatEnded = false,
  // Enterprise only: the customer's own End chat. The engineer's lives in
  // CustomerChatPanel, which never renders on that surface.
  onEndLiveChat = null,
  customerUsername = "Customer",
  tseAgentName = "",
  // Diagnostic-map snapshot, used only by the export so the downloaded record can
  // show which graphs were loaded and the path walked through them.
  diagSnapshot = null,
  // Export-only: names the zip and lets it pull this session's server-side log.
  sessionId = "",
  copilotLabel = "Copilot",
  // Idle timeout: the server has torn the session down and will not accept
  // anything under this session id, so the composer is locked until the user
  // starts a new session.
  sessionExpired = false,
  // The diagnostic session was CLOSED (feedback prompt shown). Not the same as
  // sessionExpired: the id is still valid server-side, but the conversation is
  // over, so the composer locks the same way and the post-close banner is the
  // way forward. Reuses the existing :disabled treatment — no new styling.
  sessionClosed = false,
  // Socket is down and a rejoin is in flight. The session is presumed alive, so this
  // only holds the composer briefly — unlike sessionExpired, which is terminal.
  reconnecting = false,
  // "timeout" | "disconnect" | null — picks the wording below.
  sessionEndReason = null,
  onNewSession,
  // Rejoin the SAME session rather than abandoning it. Offered for both end
  // reasons: a dropped socket resumes for real, and a genuine idle timeout is
  // refused by the server, which re-locks — so the attempt is safe either way.
  onResumeSession,
  // Keeps the banner mounted through the attempt — resuming clears
  // sessionExpired immediately, and without this the banner (and every bit of
  // feedback on it) would vanish on click.
  resumePending = false,
  copilotMode = "fast",
  onExitGuided,
  // V5: a Diagnostic Agent diagnosis is open (tse:diag_state lifecycle active/paused). The DA never switches
  // copilotMode to "guided", so the Exit bar is shown on this flag too.
  diagnosisActive = false,
  // Session learning switch (engineer surface only): on by default, per session.
  // First-prompt product correction. Absent (enterprise) means the banner is
  // read-only; productLocked is the server saying the first prompt is behind us.
  onChangeProduct,
  productLocked = false,
  // The product a change request is waiting on (null when none). The server
  // answers it; until then the control is disabled and says so.
  productChangePending = null,
  isCollapsed = false,
  onExpand,
  // Edits to a call-context block in the thread (see CallContextCard).
  // Dashboard5 never produces one, so this is never called there.
  onCallContextEdit,
  // Root Cause Analysis mode — the ActionBar toggle. Absent on Dashboard5,
  // where no call context is ever attached, so the button doesn't render.
  rcaEnabled = false,
  onToggleRca,
  // Opening a STORED session: tse:join has gone out and the transcript has not
  // arrived yet (see TseDashboard5's `hydrating`). Only ever true when the URL
  // named a session — a brand new chat still gets the welcome block below.
  hydrating = false,
  // A query has been handed to this panel and is waiting on the socket (and,
  // on a blank chat, on the server naming the session). Distinct from
  // `hydrating`, which is a STORED conversation being fetched back.
  // Title from tse:joined, so the panel can say WHICH conversation is opening.
  hydratingTitle = "",
  // The join never answered. Terminal for this attempt, but retriable.
  hydrateFailed = false,
  onRetryHydrate,
  // Observability: which storage tier the session was hydrated from.
  sessionSource = null,
  // Session context lives in the results drawer; it fetches its own state from
  // /api/memory/sessions/{id} using `sessionId` above, and ctxTurnKey is what
  // tells it a turn settled.
  ctxTurnKey,
  // Ticks on New chat — clears the panel outright, see SessionContextPanel.
  ctxResetKey = 0,
  // In-session learning overlay.
  onOpenLearning,
  // V5 banners (W7b): the diagnosis-prompt buttons (msg, confirm) and the learning_prompt button (msg);
  // the dashboard turns them into tse:action commands / opens the diagnosis popover on a tab.
  onDiagnosisPrompt,
  onLearningPromptOpen,
  // Tells the dashboard when the Cases/Bugs overlay opens or closes, so the diagnosis popover can
  // collapse to its bubble while it is open (plan 15.4).
  onResultsOpenChange,
}, ref) => {
  const navigate = useNavigate();
  const showSessionStats = useTenantFeature(FEATURE_SESSION_STATS);
  const copilotBotName = variant === "enterprise"
    ? (agentNameOverride || copilotLabel || "AI Agent")
    : "Copilot";

  const [draft, setDraft] = useState("");
  // A DA batch card has no Send of its own (owner 23 Oct): its picks live here so the composer's send button (and Enter
  // in an empty box) sends the whole batch once every question is picked. Typed text goes out with the picks made so far.
  const liveBatch = currentQuestion && currentQuestion.question !== FOLLOWUPS_MARKER && isBatchQuestion(currentQuestion)
    ? currentQuestion.questions : null;
  const batchPicks = useBatchPicks(liveBatch);
  const hasDraftText = draft.trim().length > 0;
  const batchAwaiting = Boolean(liveBatch) && !hasDraftText;
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState("");
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const attachMenuWrapRef = useRef(null);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  // Which source card the user last opened, per message id.
  const [activeSourceByMsg, setActiveSourceByMsg] = useState({});
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Which search rows have their query expanded, keyed by message id. Per-row
  // rather than one global flag: an engineer comparing two searches needs both
  // open at once to see what changed between them.
  const [openQueries, setOpenQueries] = useState({});
  // Which status row's "copy query" button just fired, keyed by message id so
  // copying one query doesn't tick every button on screen (rows are a .map()).
  const [copiedQueryId, setCopiedQueryId] = useState(null);
  const copyQueryTimerRef = useRef(null);
  const [resultsTab, setResultsTab] = useState(null);
  useEffect(() => { onResultsOpenChange?.(!!resultsTab); }, [resultsTab, onResultsOpenChange]);
  // Its own drawer, not a ResultsPanel tab: session context is not a record
  // search, and its button lives in the header level with the left pane's.
  const [contextOpen, setContextOpen] = useState(false);
  // Unread state per result type: set when a search returns a different set,
  // cleared once the user opens that tab.
  const [seenResults, setSeenResults] = useState({ bugs: true, cases: true });
  const [turnPanelOpen, setTurnPanelOpen] = useState(false);
  // Dismissal of the "Live chat ended." notice. Reset when the flag clears, so
  // a LATER live chat that ends is announced again rather than being silenced
  // by a dismissal from the previous one.
  const [liveChatNoticeDismissed, setLiveChatNoticeDismissed] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  useEffect(() => {
    if (!liveChatEnded) setLiveChatNoticeDismissed(false);
  }, [liveChatEnded]);

  /* ── Learning trail availability for the post-close banner ──────────── */
  // "hidden" — no trail yet or non-draft; "ready" — draft trail exists;
  // null — feedback_prompt not shown, skip entirely.
  const [trailBannerState, setTrailBannerState] = useState("hidden");
  const trailBannerCancelRef = useRef(null);

  const hasFeedbackPrompt = useMemo(
    () => messages.some((m) => m.type === "feedback_prompt"),
    [messages],
  );
  // The learning_prompt banner replaces the old "Review learning" probe row (16.4): once the server sent
  // one for this session, the probe row is not drawn.
  const hasLearningPrompt = useMemo(
    () => messages.some((m) => m.type === "learning_prompt"),
    [messages],
  );

  useEffect(() => {
    // No banner → nothing to probe.
    if (!hasFeedbackPrompt || !sessionId || variant === "enterprise") return;

    let active = true;
    const timers = [];

    const check = async (retryIndex) => {
      try {
        const trail = await getTrailBySession(sessionId);
        if (!active) return;
        if (trail && trail.trail?.status === "draft") {
          setTrailBannerState("ready");
          return; // stop retrying
        }
        // Trail exists but is not draft → nothing to review.
        if (trail) { setTrailBannerState("hidden"); return; }
      } catch {
        if (!active) return;
      }
      // 404 / null → capture may not have landed. Retry with backoff.
      if (retryIndex < RETRY_DELAYS_MS.length) {
        timers.push(setTimeout(() => check(retryIndex + 1), RETRY_DELAYS_MS[retryIndex]));
      }
      // After all retries exhausted, leave as "hidden" — feedback-only.
    };

    check(0);

    trailBannerCancelRef.current = () => { active = false; timers.forEach(clearTimeout); };
    return () => { active = false; timers.forEach(clearTimeout); };
  }, [hasFeedbackPrompt, sessionId, variant]);

  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  // Click-to-show popup state for TSE agent and customer avatars
  const [tseAvatarOpen, setTseAvatarOpen] = useState(false);
  const [customerAvatarOpen, setCustomerAvatarOpen] = useState(false);
  const agentRef = useRef(null);
  const tseAvatarRef = useRef(null);
  const customerAvatarRef = useRef(null);
  const resultSigRef = useRef({ bugs: "", cases: "" });
  const bodyRef = useRef(null);
  // Is autoscroll allowed to move the view? Cleared when the reader scrolls up,
  // set again when they return to the bottom or send a message. A ref, not
  // state: it is read inside the scroll effect on every streamed chunk and must
  // never cause a render of its own.
  const followRef = useRef(true);
  const lastMsgCountRef = useRef(0);
  // The anchor autoscroll has already moved to, i.e. which turn it has served.
  const anchorScrolledRef = useRef(null);
  const textareaRef = useRef(null);
  const turnPanelWrapRef = useRef(null);

  // Close avatar popups on click-outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (agentRef.current && !agentRef.current.contains(event.target)) {
        setAgentMenuOpen(false);
      }
      if (tseAvatarRef.current && !tseAvatarRef.current.contains(event.target)) {
        setTseAvatarOpen(false);
      }
      if (customerAvatarRef.current && !customerAvatarRef.current.contains(event.target)) {
        setCustomerAvatarOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // TseDashboard5's "Start Diagnosis from Call" button (CustomerChatPanel,
  // left pane) drops the condensed transcript here for the agent to review
  // before sending — same fillComposer idiom CustomerChatPanel already
  // exposes for "Edit in customer chat", mirrored in the other direction.
  useImperativeHandle(ref, () => ({
    // The diagnosis popover's Questions tab: the question is answered here, in the chat (16.4).
    focusComposer: () => {
      textareaRef.current?.focus();
    },
    fillComposer: (raw) => {
      setDraft(String(raw || ""));
      // fillComposer runs from a click handler in a sibling component, not a
      // native input event on this textarea -- setTimeout(fn, 0) fires as soon as the
      // state update is *committed*, which isn't the same as the browser
      // having *laid out* the new value yet, so scrollHeight could still
      // reflect the old (empty) content. Typing's autoResize doesn't have
      // this problem: the browser lays out its own keystroke before onChange
      // fires. Two rAFs is the standard fix -- the first runs after commit,
      // the second guarantees a layout/paint happened in between.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        autoResize(textareaRef.current);
        textareaRef.current?.focus();
      }));
    },
  }));

  // FOLLOW THE ANSWER DOWN, BUT NEVER PAST THE QUESTION THAT ASKED FOR IT.
  // A turn is read downwards from its question, so once enough answer has
  // arrived for that question to sit at the top of the viewport, the view stops
  // there and the rest of the answer streams in below a frame that no longer
  // moves. Until then it follows the bottom exactly as it always has.
  //
  // The stop is a CLAMP on the follow target, not padding added to the content.
  // a12e6d48 anchored without one and pinned the view to the top of the thread
  // for the whole first turn (the question is the FIRST block, so its offset is
  // ~0 and there is nothing to scroll); a trailing spacer sized to make the
  // question reachable fixed that but left most of the pane blank while the
  // answer was still short. min(anchor, bottom) needs neither: early in a turn
  // the bottom is the smaller number and wins, so there is never a void.
  const lastMsg = messages[messages.length - 1];
  const lastMsgId = lastMsg?.id;
  const lastMsgLen = lastMsg?.text?.length || 0;
  const lastFromEngineer = lastMsg?.sender === "user" || lastMsg?.sender === "agent";

  // The anchor is the newest ENGINEER message, found by scanning back from the
  // end rather than by looking at the last message only: the send and the
  // copilot's placeholder can land in one commit, and once the answer starts
  // streaming the last message is the copilot's for the rest of the turn. The
  // scan re-derives the same id on every chunk, so the anchor is stable for the
  // whole turn without being cached.
  let anchorMsgId;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.sender === "user" || m?.sender === "agent") { anchorMsgId = m.id; break; }
  }

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !lastMsgId) return;
    const isNewMessage = lastMsgId !== lastMsgCountRef.current;
    lastMsgCountRef.current = lastMsgId;
    // A turn is "new" when the anchor changes, not when any message arrives.
    const isNewTurn = anchorMsgId != null && anchorMsgId !== anchorScrolledRef.current;
    // Consumed here rather than after the scroll: if the reader has scrolled
    // away we return below, and this turn's anchor must not be waiting to yank
    // them back up the moment following resumes.
    if (isNewTurn) anchorScrolledRef.current = anchorMsgId;

    // Sending is asking to be taken to the answer, so it always resumes
    // following — otherwise a reader who scrolled up once would have to press
    // the jump-to-bottom button on every turn for the rest of the session.
    if (isNewMessage && lastFromEngineer) followRef.current = true;

    // The reader scrolled up to check something. Their position is theirs until
    // they come back to the bottom (or send) — see the gesture listener below.
    if (!followRef.current) return;

    const blocks = body.querySelectorAll(".d5-msg-block, .d5-status-row, .d5-product-row, .d5-error-row, .d5-clear-divider");
    const lastBlock = blocks[blocks.length - 1];
    if (!lastBlock) return;

    // The divider must land flush at the top with the old conversation fully
    // above the fold. offsetTop is measured against each element's
    // offsetParent, which is not this scroller, so the arithmetic below lands
    // it a hundred-odd pixels low and leaks the tail of the last answer.
    if (lastBlock.classList.contains("d5-clear-divider")) {
      lastBlock.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    // No engineer bubble on screen (a replayed turn whose reply never
    // rendered): fall through to the plain bottom-follow rather than freezing.
    const anchor = findEngineerBlock(body, anchorMsgId);

    // Rect-based, not offsetTop (the one part of a12e6d48 worth keeping):
    // offsetTop is measured against the nearest positioned ancestor, which is
    // .d5-copilot-content, not this scroller — the same reason the divider
    // above needs scrollIntoView.
    const anchorTarget = anchor
      ? anchor.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop
        - anchorTopGap(body)
      : Infinity;
    // Where following the answer down would put us.
    const bottomTarget = body.scrollHeight - body.clientHeight;
    // The clamp, and the whole fix: while the answer is short the bottom is
    // nearer than the question, so this follows generation. As soon as enough
    // has streamed for the question to reach the top, the question is nearer
    // and the target stops moving for the rest of the turn, however much more
    // arrives.
    const target = Math.max(0, Math.min(Math.round(anchorTarget), bottomTarget));
    // Forward only: a growing bubble must not yank the view backwards.
    if (target > body.scrollTop || isNewTurn) {
      body.scrollTo({ top: target, behavior: isNewTurn ? "smooth" : "auto" });
    }
  }, [anchorMsgId, lastMsgId, lastMsgLen, lastFromEngineer]);

  // WHO OWNS THE SCROLL POSITION — the reader, or the arriving answer.
  //
  // Scrolling UP is the signal: it is only ever deliberate, and it means "hold
  // still, I am reading something back". Following resumes when they return to
  // the bottom, which is the same "caught up" line the jump-to-bottom button
  // uses, so the button disappearing and following resuming are one event.
  //
  // Driven by INPUT GESTURES, not by watching scrollTop. Our own scrollTo fires
  // scroll events too — and a `smooth` one fires a stream of them at positions
  // short of the target — so position-watching cannot tell the reader's scroll
  // from the panel's own and would pause following against itself.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;

    const atBottom = () =>
      body.scrollHeight - body.scrollTop - body.clientHeight <= SCROLL_BOTTOM_SLACK;
    const decide = (wentUp) => {
      if (wentUp) followRef.current = false;
      else if (atBottom()) followRef.current = true;
    };

    const onWheel = (e) => decide(e.deltaY < 0);

    // Touch is inverted: dragging the finger DOWN pulls the content down, which
    // moves the view up the thread.
    let touchY = 0;
    const onTouchStart = (e) => { touchY = e.touches[0]?.clientY ?? 0; };
    const onTouchMove = (e) => {
      const y = e.touches[0]?.clientY ?? 0;
      decide(y > touchY);
      touchY = y;
    };

    // Keyboard scrolling is deliberately not handled: the scroller is a plain
    // div with no tabIndex, so it never receives keydown, and focus normally
    // sits in the composer where the arrow keys move the caret instead. A
    // listener here would be dead code that reads as coverage.
    //
    // Dragging the scrollbar produces no wheel event, so it is read from
    // scrollTop — but ONLY between pointerdown and pointerup, which is the one
    // window in which a scroll cannot have come from our own scrollTo.
    let dragging = false;
    let dragFrom = 0;
    const onPointerDown = () => { dragging = true; dragFrom = body.scrollTop; };
    const onPointerUp = () => { dragging = false; };
    const onScroll = () => {
      if (!dragging) return;
      decide(body.scrollTop < dragFrom);
      dragFrom = body.scrollTop;
    };

    body.addEventListener("wheel", onWheel, { passive: true });
    body.addEventListener("touchstart", onTouchStart, { passive: true });
    body.addEventListener("touchmove", onTouchMove, { passive: true });
    body.addEventListener("scroll", onScroll, { passive: true });
    body.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    return () => {
      body.removeEventListener("wheel", onWheel);
      body.removeEventListener("touchstart", onTouchStart);
      body.removeEventListener("touchmove", onTouchMove);
      body.removeEventListener("scroll", onScroll);
      body.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  // GPT-style affordance: show a jump-to-bottom control only while content is
  // hidden below the fold.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const update = () => {
      const remaining = body.scrollHeight - body.scrollTop - body.clientHeight;
      setShowScrollDown(remaining > SCROLL_BOTTOM_SLACK);
    };
    update();
    body.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(body);
    return () => { body.removeEventListener("scroll", update); observer.disconnect(); };
    // Re-run on every streamed chunk, not just on new messages. Once the view
    // locks at the anchor the answer keeps growing with NO scroll event and no
    // resize of the scroller's own box (the ResizeObserver watches the body,
    // whose size never changes), so this is the only thing that notices content
    // going below the fold — which is exactly when the button is needed.
  }, [messages.length, lastMsgId, lastMsgLen]);

  // Compare result identity, not just count: a background search that swaps in
  // a different set of the same size is still new information.
  useEffect(() => {
    const signature = (items) =>
      items.map((i) => i.bug_id || i.case_number || i.case_id || i.id || i.title || "").join("|");
    const next = { bugs: signature(bugs), cases: signature(cases) };
    const changed = {};
    if (bugs.length && next.bugs !== resultSigRef.current.bugs) changed.bugs = false;
    if (cases.length && next.cases !== resultSigRef.current.cases) changed.cases = false;
    resultSigRef.current = next;
    if (Object.keys(changed).length) setSeenResults((prev) => ({ ...prev, ...changed }));
  }, [bugs, cases]);

  const openResults = (tab) => {
    setResultsTab(tab);
    setSeenResults((prev) => ({ ...prev, [tab]: true }));
  };

  // Close turn panel on click-outside.
  useEffect(() => {
    if (!turnPanelOpen) return undefined;
    const close = (e) => {
      if (turnPanelWrapRef.current && !turnPanelWrapRef.current.contains(e.target)) {
        setTurnPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [turnPanelOpen]);

  // Close the attach menu on click-outside, same idiom as the turn panel above.
  useEffect(() => {
    if (!attachMenuOpen) return undefined;
    const close = (e) => {
      if (attachMenuWrapRef.current && !attachMenuWrapRef.current.contains(e.target)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [attachMenuOpen]);

  const scrollToBottom = () => {
    const body = bodyRef.current;
    if (!body) return;
    // Pressing this IS "catch me up", so it resumes following as well as
    // scrolling -- otherwise the answer keeps growing below the fold and the
    // button the reader just pressed comes straight back.
    followRef.current = true;
    body.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT) + "px";
  };

  // Attachments hold an object URL for image previews (see attachments.js);
  // release whatever is still attached when the panel unmounts. Anything
  // that made it into a sent message lives on in that message's own
  // `attachments` array and isn't touched here.
  useEffect(() => () => {
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(copyQueryTimerRef.current), []);

  const handleCopyQuery = (msgId, query) => {
    navigator.clipboard?.writeText(query);
    setCopiedQueryId(msgId);
    clearTimeout(copyQueryTimerRef.current);
    copyQueryTimerRef.current = setTimeout(() => setCopiedQueryId(null), 1500);
  };

  const handleFilesSelected = (fileList) => {
    const roomLeft = MAX_ATTACHMENTS - attachments.length;
    const { accepted, rejected } = triageFiles(fileList, roomLeft);
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
    setAttachError(rejected.length ? `${rejected.join(" · ")} (supported: ${SUPPORTED_FORMATS_LABEL})` : "");
  };

  // ── drag and drop ────────────────────────────────────────────────────────
  // The whole panel is the drop target, not just the composer: a file dragged
  // over a full transcript would otherwise have a strip a few pixels tall to
  // hit, and the browser's default action for a miss is to NAVIGATE AWAY to the
  // dropped file — losing the session. onDragOver/onDrop are therefore bound at
  // the panel root and always preventDefault, whether or not we accept it.
  const dragDepthRef = useRef(0);

  const dragHasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  /**
   * Open an attached capture in the analyzer.
   *
   * The file is staged in IndexedDB (same origin) and the analyzer picks it up
   * from the token in the URL -- a File cannot travel in a query string, and
   * making the engineer re-pick the file they just attached is the thing this
   * exists to avoid.
   *
   * A NEW TAB, deliberately: the attachment is still in the composer and the
   * turn has not been sent. Navigating away in this tab would lose both.
   *
   * If staging fails (private browsing, quota) the analyzer still opens, just
   * empty -- one file-picker click, never a dead end.
   */
  /**
   * Captures in this conversation the analyzer can open, newest first.
   *
   * Guarded on `a.file`, not just the extension. A message keeps the very
   * objects the composer held (handleSend passes them to appendMessage, which
   * spreads them in), so the File is live for this session -- but a transcript
   * rehydrated from server history has none, and an offer that cannot be
   * honoured is worse than no offer.
   */
  const analyzableAttachments = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      for (const a of messages[i].attachments || []) {
        if (a.file && isAnalyzable(a.name) && !seen.has(a.id)) {
          seen.add(a.id);
          out.push(a);
        }
      }
    }
    return out;
  }, [messages]);

  // Only the LAST suggestions card carries the offer. Repeating it under every
  // one turns a single action into wallpaper, and the newest card is the one the
  // engineer is looking at.
  const lastFollowupsIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i]?.type === "followups") return i;
    return -1;
  }, [messages]);

  const openInAnalyzer = useCallback(async (attachment) => {
    const url = await stageForAnalyzer(attachment?.file);
    window.open(url || "/analyzer/", "_blank", "noopener");
  }, []);

  const canAttach = !isStreaming && !sessionExpired && !sessionClosed && !hydrating && attachments.length < MAX_ATTACHMENTS;

  const handleDragEnter = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    // Depth counter, not a boolean: dragging over a child fires dragleave on
    // the parent, so a naive flag flickers the overlay off mid-drag.
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = canAttach ? "copy" : "none";
  };

  const handleDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (!canAttach) {
      setAttachError(
        isStreaming ? "Copilot is responding — wait for the answer, then attach."
          : sessionExpired ? "Session timed out — start a new session to attach."
          : sessionClosed ? "Session closed — start a new session to attach."
          : `Attach up to ${MAX_ATTACHMENTS} files`,
      );
      return;
    }
    handleFilesSelected(e.dataTransfer.files);
  };

  // Rejection notices read like a toast, not a permanent fixture — clear
  // themselves shortly after the most recent one lands.
  useEffect(() => {
    if (!attachError) return undefined;
    const timer = setTimeout(() => setAttachError(""), 2500);
    return () => clearTimeout(timer);
  }, [attachError]);

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = () => {
    const text = draft.trim();
    const liveChatLocked = variant === "enterprise" && liveChatEnded;
    // A prompt is required either way; attachments ride along with it but
    // never substitute for it. Clicking send with an empty box focuses it
    // rather than doing nothing at all, since the button now stays live.
    // A click during hydrate targets a session whose transcript has not
    // loaded yet — there is nothing to send it into.
    const batchAnswer = !text && liveBatch ? batchPicks.answer() : null;
    if (batchAnswer && !isStreaming && !sessionExpired && !sessionClosed && !hydrating && !liveChatLocked) {
      onAnswer?.(batchAnswer);
      return;
    }
    if (!text || isStreaming || sessionExpired || sessionClosed || hydrating || liveChatLocked) {
      if (!text && !isStreaming && !sessionExpired && !sessionClosed && !hydrating && !liveChatLocked) textareaRef.current?.focus();
      return;
    }
    // Picks staged on a batch card go with the typed text (F5-02): never dropped in favour of the text.
    const staged = liveBatch ? buildPartialAnswers({ questions: liveBatch }, batchPicks.picks) : [];
    if (staged.length) onSend?.(text, attachments, staged);
    else onSend?.(text, attachments);
    setDraft("");
    setAttachments([]);
    setAttachError("");
    // Clearing the value doesn't undo the inline height a long prompt grew to,
    // so the box would stay tall over an empty composer.
    const ta = textareaRef.current;
    if (ta) ta.style.height = COMPOSER_MIN_HEIGHT;
  };

  const handleAnswer = (answer) => {
    onAnswer?.(answer);
  };

  const isEmpty = messages.length === 0 && !isStreaming;

  return (
    <div
      className={`d5-copilot-panel${isDragOver ? " d5-copilot-panel--dragover" : ""}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="d5-drop-overlay">
          <div className="d5-drop-overlay-inner">
            <Paperclip size={22} />
            <span className="d5-drop-overlay-title">
              {canAttach ? "Drop to attach" : "Cannot attach right now"}
            </span>
            <span className="d5-drop-overlay-sub">{SUPPORTED_FORMATS_LABEL}</span>
          </div>
        </div>
      )}
      {/* The enterprise header wears the cyan wash so the two surfaces cannot
          be mistaken for each other at a glance. Engineers run both side by
          side - the customer's chat on the left, their own copilot on the
          right - and identical grey bars made it easy to read one as the
          other. Same --arta-primary-light the Context button already uses, so
          this introduces no new colour. */}
      <div className={`d5-copilot-header${variant === "enterprise" ? " d5-copilot-header-ent" : ""}`}>
        <div className="d5-copilot-header-titles">
          {isCollapsed && onExpand && (
            <button
              type="button"
              className="d5-expand-button"
              onClick={onExpand}
              title="Expand Customer Chat"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                color: "var(--arta-text-muted)",
                marginRight: "6px"
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {variant !== "enterprise" && (
            <div className="d5-panel-icon d5-panel-icon-copilot">
              <Bot size={15} />
            </div>
          )}
          {variant !== "enterprise" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <h2 className="d5-copilot-title">Copilot</h2>
              {/* Session-source badge (NEW / RAM / REDIS / POSTGRES) — a debugging
                  aid for the resume work, not wanted in prod. Commented out rather
                  than deleted so it can be switched back on locally when the
                  hydration path needs inspecting.
              {sessionSource && (() => {
                // Storage tier WINS over from_ram. The client emits tse:join
                // twice per page load; the second join finds the session in RAM
                // (from_ram=true) even though the transcript on screen was read
                // from Redis/Postgres, so preferring RAM relabelled every resume
                // as "RAM" and hid the very thing this badge exists to show.
                // from_ram is only the answer when no storage tier produced a
                // bundle at all.
                const label = sessionSource.read_src === "redis" ? "REDIS"
                  : sessionSource.read_src === "pg" ? "POSTGRES"
                  : sessionSource.from_ram ? "RAM"
                  : sessionSource.read_src === "none" ? "NEW"
                  : null;
                const ms = sessionSource.read_ms ?? 0;
                // `hydrated` means the AGENT was rebuilt from storage; a re-join
                // on a live session only re-reads the transcript. Same tier,
                // very different event — say which one happened.
                const tip = label === "RAM"
                  ? "live in RAM"
                  : label === "NEW"
                    ? "new session - nothing in storage"
                    : sessionSource.hydrated
                      ? `agent hydrated from ${label.toLowerCase()} in ${ms} ms`
                      : `transcript re-read from ${label.toLowerCase()} in ${ms} ms`;
                return <span className="d5-session-source-badge" title={tip}>{label}</span>;
              })()}
              */}
            </div>
            <p className="d5-copilot-subtitle">
              AI-powered TSE assistant
            </p>
          </div>
          )}
        </div>
        <div className="d5-copilot-header-meta">
          {/* End chat, for the CUSTOMER. On /ent-user the left pane never
              renders (TseDashboard5 wraps it in `{!embedded && …}`), so the
              End chat button that lives in its header does not exist on this
              surface — the customer had no way to leave a live chat they did
              not ask to stay in. Same event as the engineer's button; the
              server tells the two callers apart and hands the customer back to
              the AI instead of ending the session.
              Only while a human is actually attending: no escalation, nothing
              to end. */}
          {variant === "enterprise" && escalationActive && !liveChatEnded && onEndLiveChat && (
            <button
              type="button"
              className="d5-customer-end-chat-btn"
              onClick={() => setShowEndChatConfirm(true)}
              title="End the live chat and return to the AI assistant"
            >
              <Square size={11} fill="currentColor" />
              End chat
            </button>
          )}
          {sessionStats.turns > 0 && showSessionStats && (
            <TurnBreakdownPanel
              turnLog={sessionStats.turnLog}
              fallbackTurns={sessionStats.turns}
              open={turnPanelOpen}
              onToggle={() => setTurnPanelOpen((v) => !v)}
              wrapRef={turnPanelWrapRef}
            />
          )}
          {messages.length > 0 && (
            <button
              type="button"
              className="d5-export-btn"
              title="Export conversation as Markdown"
              onClick={() => downloadSessionBundle(messages, sessionStats, {
                bugs, cases, resultsMeta, currentQuestion, diagSnapshot, sessionId,
              })}
            >
              <Download size={11} />
              Export
            </button>
          )}
          {/* Level with the left pane's Context chip — the two contexts the
              engineer can inspect are opened from the same row on both sides. */}
          <button
            type="button"
            className="d5-header-ctx-btn"
            title="Session context — what the copilot remembers about this session"
            onClick={() => setContextOpen(true)}
          >
            <Brain size={11} strokeWidth={2} />
            Context
          </button>
          {variant !== "enterprise" && onOpenLearning && (
            <button
              type="button"
              className="d5-header-ctx-btn"
              title="Review and submit what this session taught us"
              aria-label="Review learning"
              onClick={() => onOpenLearning?.()}
            >
              <GraduationCap size={11} strokeWidth={2} />
              Learning
            </button>
          )}
          {variant !== "enterprise" && (
            <span className="d5-feedback-wrap">
              <button
                type="button"
                className="d5-feedback-btn"
                title="Share feedback on this session"
                onClick={() => onOpenFeedback?.()}
              >
                <MessageSquareWarning size={11} />
                Feedback
              </button>
              {feedbackNotice && <span className="d5-feedback-notice">{feedbackNotice}</span>}
            </span>
          )}
          {variant === "enterprise" ? (
            /* Case A: Enterprise User view - AI Agent (Emma), TSE Agent (Chris if escalated), Customer menu */
            <>
              {/* AI Agent Avatar (Emma, Sarah, etc.) */}
              {agentNameOverride && (
                <div ref={agentRef} className="relative flex items-center">
                  <button
                    type="button"
                    onClick={() => setAgentMenuOpen(!agentMenuOpen)}
                    className={`d5-avatar-btn d5-avatar-ai ${escalationActive ? "d5-avatar-disabled" : ""}`}
                    title={`${agentNameOverride} (AI Agent)`}
                  >
                    {agentNameOverride.charAt(0).toUpperCase()}
                  </button>
                  {agentMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-[1000] text-left">
                      <div className="flex flex-col gap-2">
                        <div>
                          <div className="text-xs font-semibold text-[#06b6d4] uppercase tracking-wide mb-0.5">AI Agent</div>
                          <div className="text-sm font-medium text-gray-800">{agentNameOverride}</div>
                        </div>
                        {(typeof sentiment === 'number' || typeof prevSentiment === 'number') && (
                          <div className="border-t border-gray-100 pt-2 flex flex-col gap-1 text-xs text-[#253439]">
                            <div>Previously: <strong className="font-semibold text-gray-700">{typeof prevSentiment === 'number' ? prevSentiment.toFixed(1) : prevSentiment}/10</strong></div>
                            <div>Currently: <strong className="font-semibold text-gray-900">{typeof sentiment === 'number' ? sentiment.toFixed(1) : sentiment}/10</strong></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TSE Agent Avatar (Chris) - visible only when escalated */}
              {escalationActive && (
                <div ref={tseAvatarRef} className="relative flex items-center">
                  <button
                    type="button"
                    onClick={() => setTseAvatarOpen(!tseAvatarOpen)}
                    className="d5-avatar-btn d5-avatar-tse"
                    title={`${tseAgentName || "Support Engineer"} (Support Engineer)`}
                  >
                    {(tseAgentName || "Agent").charAt(0).toUpperCase()}
                  </button>
                  {tseAvatarOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-[1000] text-left">
                      <div className="flex flex-col gap-1">
                        <div className="text-xs font-semibold text-[#06b6d4] uppercase tracking-wide mb-1">Support Engineer</div>
                        <div className="text-sm font-medium text-gray-800">{tseAgentName || "Support Engineer"} (Support Engineer)</div>
                        <div className="text-xs text-gray-500">Live agent connected</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* User Profile Avatar - Burgundy Red */}
              {onLogout && <UserProfileMenu onLogout={onLogout} variant="enterprise" sentiment={sentiment} prevSentiment={prevSentiment} />}
            </>
          ) : (
            /* Case B: Standard TSE Agent view - Customer avatar (Burgundy if escalated), TSE Agent menu (Chris) */
            <>
              {/* Customer Profile Circle Avatar (Burgundy Red) - visible only when escalated */}
              {escalationActive && (
                <div ref={customerAvatarRef} className="relative flex items-center">
                  <button
                    type="button"
                    onClick={() => setCustomerAvatarOpen(!customerAvatarOpen)}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#800020",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      border: "none",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    title={`${customerUsername || "Customer"} (User)`}
                  >
                    {(customerUsername || "Customer").charAt(0).toUpperCase()}
                  </button>
                  {customerAvatarOpen && (
                    <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-[1000] text-left">
                      <div className="flex flex-col gap-1">
                        <div className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-1">User</div>
                        <div className="text-sm font-medium text-gray-800">{customerUsername || "Customer"}</div>
                        <div className="text-xs text-gray-500">Enterprise user connected</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TSE Agent Profile Menu (Standard Blue) */}
              {/* The engineer reads the handoff summary, and the summary no
                  longer carries a sentiment meter — so this is now their only
                  reading of how the customer feels. Same two lines as the
                  enterprise menu above. */}
              {onLogout && <UserProfileMenu onLogout={onLogout} variant="tse" sentiment={sentiment} prevSentiment={prevSentiment} />}
            </>
          )}
        </div>
      </div>

      {!(escalationActive && variant === "enterprise") && (
      <ActionBar
        onAction={onAction}
        onCaseAnalysisSubmit={onCaseAnalysisSubmit}
        variant={variant}
        // A click during hydrate targets a session whose transcript has not
        // loaded yet, so the whole bar stays inert until it has. Closed and
        // expired lock it for the same reason the composer locks: every action
        // here dispatches a turn, and a closed diagnostic would be silently
        // REVIVED by one (server-side _revive_if_ended) — which is not what the
        // engineer meant by closing it.
        //
        // isStreaming locks it for a different reason: every button here starts
        // a turn, and a second turn on top of a running one is refused by the
        // per-session lock server-side. The composer and the attach control
        // already block while streaming — this bar was the only way left to
        // fire a turn mid-answer, and doing so read as a dead click.
        disabled={hydrating || sessionClosed || sessionExpired || isStreaming}
        disabledReason={
          sessionExpired
            ? "Session timed out — start a new session to use this."
            : sessionClosed
            ? "Session closed — start a new session to use this."
            : isStreaming
            ? "Copilot is responding — wait for the answer, then try again."
            : ""
        }
        bugCount={bugs.length}
        caseCount={cases.length}
        searched={{ bugs: !!resultsMeta.bugs?.searched, cases: !!resultsMeta.cases?.searched }}
        unseen={{ bugs: !seenResults.bugs, cases: !seenResults.cases }}
        onOpenResults={openResults}
        actionNotice={actionNotice}
        rcaEnabled={rcaEnabled}
        onToggleRca={onToggleRca}
      />
      )}

      <div className="d5-copilot-content">
      <div ref={bodyRef} className="d5-copilot-body" onClick={(e) => {
        const imgLink = e.target.closest("a.image-link");
        if (imgLink) { e.preventDefault(); const u = imgLink.getAttribute("data-image-url"); if (u) setZoomImageUrl(u); return; }
        const docLink = e.target.closest("a.citation-link");
        if (docLink) { e.preventDefault(); const du = docLink.getAttribute("data-doc-url"); if (du) openDocument(du, onDocError); return; }
        // Inline [S1] pill: highlight the card it points at and open that doc.
        const pill = e.target.closest(".d5-cite-pill");
        if (pill) {
          e.preventDefault();
          const msgId = pill.closest(".d5-msg-block")?.getAttribute("data-msg-id");
          const msg = messages.find((m) => String(m.id) === String(msgId));
          const idx = Number(pill.getAttribute("data-cite-index"));
          const src = msg?.sources?.[idx];
          if (src) {
            const number = src.n || idx + 1;
            // The card may live on an earlier turn — this document's card is only
            // drawn the first time it appears — so highlight it wherever it is.
            const owner = messages.find((m) =>
              (m.sources || []).some((s) => (s.n || 0) === number && s.is_new !== false)
            ) || msg;
            setActiveSourceByMsg((prev) => ({ ...prev, [owner.id]: number }));
            // `citation` first: it is `url` plus the "#page=N" of the chunk the
            // answer actually cited, and openDocument reattaches that hash to
            // the blob it fetches. Preferring `url` opened every runbook at
            // page 1 and quietly discarded the page anchor we compute.
            openDocument(src.citation || src.url, onDocError);
          }
        }
      }}>
        {/* Three mutually exclusive states for an empty transcript: a stored
            session still loading, one whose join never answered, and a genuinely
            new chat. Before this, all three drew "Ready to diagnose", so opening
            a stored session looked like an empty one for the 10-20s the replay
            takes. */}
        {isEmpty && hydrating && (
          <div className="d5-copilot-hydrating" role="status" aria-live="polite">
            <div className="d5-hydrating-head">
              <span className="d5-hydrating-label">
                <Loader2 size={14} className="d5-hydrating-spinner" aria-hidden="true" />
                Loading conversation…
              </span>
              {hydratingTitle && <p className="d5-hydrating-name" title={hydratingTitle}>{hydratingTitle}</p>}
            </div>
            {/* Placeholders in the shape of the bubbles that will replace them,
                so the panel doesn't jump when the transcript lands. */}
            <div className="d5-hydrating-rows" aria-hidden="true">
              <div className="d5-hydrating-row d5-hydrating-row-user">
                <span className="d5-hydrating-bar" />
              </div>
              <div className="d5-hydrating-row">
                <span className="d5-hydrating-bar" />
                <span className="d5-hydrating-bar" />
                <span className="d5-hydrating-bar" />
              </div>
              <div className="d5-hydrating-row d5-hydrating-row-user">
                <span className="d5-hydrating-bar" />
              </div>
              <div className="d5-hydrating-row">
                <span className="d5-hydrating-bar" />
                <span className="d5-hydrating-bar" />
              </div>
            </div>
          </div>
        )}

        {isEmpty && !hydrating && hydrateFailed && (
          <div className="d5-copilot-welcome">
            <div className="d5-copilot-welcome-icon">
              <Bot size={28} />
            </div>
            <h3 className="d5-copilot-welcome-title">Couldn&apos;t load this conversation</h3>
            <p className="d5-copilot-welcome-hint">
              The session didn&apos;t respond in time. You can retry, or just start typing to continue it.
            </p>
            <button type="button" className="d5-hydrating-retry" onClick={() => onRetryHydrate?.()}>
              <RotateCcw size={12} />
              Retry
            </button>
          </div>
        )}

        {isEmpty && !hydrating && !hydrateFailed && (
          <div className="d5-copilot-welcome">
            <div className="d5-copilot-welcome-icon">
              <Bot size={28} />
            </div>
            <h3 className="d5-copilot-welcome-title">Ready to diagnose</h3>
            <p className="d5-copilot-welcome-hint">Describe an issue or use an action above to begin</p>
          </div>
        )}

        {!isEmpty && messages.map((msg, i) => {
          if (escalationActive && variant === "enterprise") {
            const statusText = String(msg.text || "");
            if (
              msg.type === "status"
              || msg.isStreaming
              || /^Searching search_/i.test(statusText)
              || (/\bSearching\b/i.test(statusText) && /\bsearch_(bugs|cases)\b/i.test(statusText))
            ) {
              return null;
            }
          }
          // A replayed system line: "escalated to a live agent", "<name>
          // joined", "the live chat ended". Its own type rather than "status"
          // because the enterprise branch above HIDES status rows while an
          // escalation is active — which is exactly when these are the only
          // explanation the customer has for what happened to their chat.
          if (msg.type === "system_note") {
            return (
              <div
                key={msg.id || i}
                className="d5-msg-block"
                data-msg-id={msg.id}
                style={{ textAlign: "center", margin: "10px 0" }}
              >
                <span style={{ fontSize: "12px", opacity: 0.65 }}>{msg.text}</span>
              </div>
            );
          }
          if (msg.type === "error") {
            return (
              <div key={msg.id || i} className="d5-error-row">
                <span className="d5-error-icon">!</span>
                <span className="d5-error-text">{msg.text}</span>
              </div>
            );
          }
          if (msg.type === "call_context") {
            // Sits in the thread where it was added, so the order of "what the
            // agent was told" and "what was asked" stays readable. Editing it
            // rewrites what the agent sees from the next turn on.
            return (
              <CallContextCard
                key={msg.id || i}
                text={msg.text}
                superseded={!!msg.superseded}
                onSave={(next) => onCallContextEdit?.(msg.id, next)}
              />
            );
          }
          if (msg.type === "product") {
            const products = Array.isArray(msg.products) ? msg.products : [];
            const editable =
              !!onChangeProduct &&
              msg.canChange &&
              !productLocked &&
              !sessionExpired &&
              !sessionClosed &&
              products.length > 1 &&
              !messages.slice(i + 1).some((m) => m.type === "product");
            return (
              <div
                key={msg.id || i}
                className={`d5-product-row${editable ? " d5-product-row--changeable" : ""}`}
              >
                <span className="d5-product-label">Product identified</span>
                <span className="d5-product-badge">{msg.product}</span>
                {editable && (
                  <span
                    className="d5-product-change"
                    data-tip="Pick the correct product and your first prompt is re-run with it"
                  >
                    <span className="d5-product-change-hint" aria-live="polite">
                      {productChangePending ? `Switching to ${productChangePending}…` : "Wrong product?"}
                    </span>
                    <span className="d5-product-change-picker">
                      <select
                        className="d5-product-change-linkselect"
                        value=""
                        aria-label="Change product"
                        disabled={!!productChangePending}
                        onChange={(e) => e.target.value && onChangeProduct(msg.id, e.target.value)}
                      >
                        <option value="" disabled hidden>Change here</option>
                        {products.filter((p) => p !== msg.product).map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <ChevronDown size={10} className="d5-product-change-chevron" aria-hidden="true" />
                    </span>
                  </span>
                )}
              </div>
            );
          }
          if (msg.type === "followups") {
            const opts = Array.isArray(msg.options) ? msg.options : [];
            if (!opts.length) return null;
            // Suggestions, not a question: they sit in the transcript like any
            // other message instead of taking over the composer. Options stay
            // clickable until one is picked; the ordinal is presentational
            // only, the click still sends the raw option text.
            const disabled = !!msg.answered || isStreaming || sessionExpired;
            // The walkthrough option is not a suggestion like the others: the
            // rest ask the copilot another question and stay in this
            // conversation, while this one switches the session into guided
            // mode. Banner design Option A (owner, 22 Oct): it leaves the
            // numbered list for a tinted footer band with its own explanation.
            // Matched on the server's own label (tse_session._STEP_BY_STEP_LABEL);
            // the click still sends that exact text through onAnswer.
            const isGuidedOpt = (opt) => String(opt).trim().toLowerCase() === STEP_BY_STEP_LABEL.toLowerCase();
            const questions = opts.filter((opt) => !isGuidedOpt(opt));
            const guided = opts.find(isGuidedOpt);
            // "Show in analyzer" belongs to the same moment (the engineer has
            // just read what the copilot made of the capture), but it opens a
            // different app in a new tab and never calls onAnswer. Only on the
            // newest suggestions card, and only while a capture with a live
            // File is in the transcript.
            const analyzers = i === lastFollowupsIndex ? analyzableAttachments : [];
            const offerState = !msg.answered
              ? OFFER_STATES.IDLE
              : (msg.selected === guided ? OFFER_STATES.ACCEPTED : OFFER_STATES.DECLINED);
            return (
              <div key={msg.id || i} className="d5-msg-block d5-followups-block" data-msg-id={msg.id}>
                <div className={`d5-followups-card${msg.answered ? " d5-followups-card--answered" : ""}`}>
                  <div className="d5-followups-title">Helpful suggestions</div>
                  {questions.length > 0 && (
                    <div className="d5-followups-options" role="group" aria-label="Suggested followups">
                      {questions.map((opt, oi) => {
                        const selected = msg.answered && msg.selected === opt;
                        return (
                          <button
                            key={oi}
                            type="button"
                            className={`d5-followup-option${selected ? " d5-followup-option-picked" : ""}`}
                            disabled={disabled}
                            aria-pressed={selected}
                            title={opt}
                            onClick={() => onAnswer?.(opt)}
                          >
                            <span className="d5-followup-num">{oi + 1}.</span>
                            <span className="d5-followup-text">{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(guided || analyzers.length > 0) && (
                    <div className="d5-followups-footer" role="group" aria-label="Other ways forward">
                      {guided && (
                        <GuidedOfferRow
                          label={guided}
                          subId={`d5-offer-sub-${msg.id || i}`}
                          offerState={offerState}
                          disabled={isStreaming || sessionExpired}
                          onSelect={(label) => onAnswer?.(label)}
                        />
                      )}
                      {analyzers.map((a) => (
                        <button
                          key={`analyze-${a.id}`}
                          type="button"
                          className="d5-followup-option d5-followup-option-analyze d5-offer-row"
                          title={`Open ${a.name} in the analyzer`}
                          onClick={() => openInAnalyzer(a)}
                        >
                          <span className="d5-offer-icon d5-followup-analyze-icon" aria-hidden="true"><BarChart3 size={12} /></span>
                          <span className="d5-offer-body">
                            <span className="d5-followup-text d5-offer-label">Show {a.name} in analyzer</span>
                          </span>
                          <span className="d5-offer-trail" aria-hidden="true"><ChevronRight size={14} /></span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="d5-msg-meta">
                  <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                    {capName(copilotBotName)} {formatTimestamp(msg.askedAt || msg.timestamp)}
                  </span>
                </div>
              </div>
            );
          }
          // tse:diagnosis_prompt (W7b): the small "switch to automatic" offer.
          // Never a question card option; the pending question stays answerable.
          if (msg.type === "diagnosis_prompt") {
            return (
              <DiagnosisPromptBanner
                key={msg.id || i}
                msg={msg}
                disabled={sessionExpired || sessionClosed}
                onConfirm={(m) => onDiagnosisPrompt?.(m, true)}
                onDismiss={(m) => onDiagnosisPrompt?.(m, false)}
              />
            );
          }
          // learning_prompt (plan 15.5, 16.4): same row family as the feedback
          // row, directly under it when both arrive; suppressed on enterprise
          // like the feedback row.
          if (msg.type === "learning_prompt") {
            if (variant === "enterprise") return null;
            return (
              <LearningPromptRow
                key={msg.id || i}
                msg={msg}
                onOpenTab={onLearningPromptOpen}
              />
            );
          }
          if (msg.type === "learning_note") {
            if (variant === "enterprise") return null;
            return (
              <div key={msg.id || i} className={`d5-learning-note${msg.enabled ? " is-on" : " is-off"}`} role="note">
                <GraduationCap size={11} strokeWidth={2} aria-hidden="true" />
                <span>{msg.text}</span>
              </div>
            );
          }
          if (msg.type === "status") {
            if (escalationActive && variant === "enterprise") return null;
            // A search status carries the QUERY it ran. Behind a disclosure, not
            // inline: for cases and bugs it is a paragraph-long factual summary
            // of the conversation, and pasting that into the thread on every
            // search would bury the answer it is meant to explain.
            const q = (msg.query || "").trim();
            const open = !!openQueries[msg.id];
            return (
              <div key={msg.id || i} className="d5-status-row">
                <span className="d5-status-dot" />
                <span className="d5-status-text">{msg.text}</span>
                {q && (
                  <>
                    <button
                      type="button"
                      className="d5-status-query-toggle"
                      onClick={() => setOpenQueries((prev) => ({ ...prev, [msg.id]: !prev[msg.id] }))}
                      title={open ? "Hide the query" : "Show the query this search ran"}
                      aria-expanded={open}
                    >
                      <ChevronDown size={10} className={`d5-status-query-toggle-chevron${open ? " d5-status-query-toggle-chevron-open" : ""}`} />
                      {open ? "hide query" : "show query"}
                    </button>
                    {open && (
                      <div className="d5-status-query">
                        <button
                          type="button"
                          className="d5-status-query-copy"
                          onClick={() => handleCopyQuery(msg.id, q)}
                          title="Copy query"
                          aria-label="Copy query"
                        >
                          {copiedQueryId === msg.id ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                        {q}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          }
          // Post-close banners. The feedback banner is the original Dashboard2-
          // parity prompt (server text + "Open Feedback Form"), unchanged. When a
          // draft learning trail exists, a SEPARATE learning banner is stacked
          // under it; it never replaces or alters the feedback banner.
          if (msg.type === "feedback_prompt") {
            // Both surfaces, and deliberately WORD FOR WORD the same on each.
            // This row was enterprise-suppressed, which left a closed customer
            // chat with no feedback path at all: the header Feedback button is
            // engineer-only too, so the row is the only way in.
            //
            // It briefly carried customer-specific wording. One row, one
            // sentence, both surfaces: the text is server-written and arrives
            // in `msg.text`, so a change there reaches everyone at once
            // instead of leaving two strings to keep in step.
            if (variant === "enterprise") return null;
            const showLearning = trailBannerState === "ready" && onOpenLearning && !hasLearningPrompt;
            return (
              <React.Fragment key={msg.id || i}>
                <div className="d5-feedback-prompt-row">
                  <span className="d5-feedback-prompt-text">{msg.text}</span>
                  <button
                    type="button"
                    className="d5-feedback-prompt-btn"
                    onClick={() => onOpenFeedback?.()}
                  >
                    Open Feedback Form
                  </button>
                </div>
                {showLearning && (
                  <div className="d5-feedback-prompt-row">
                    <span className="d5-feedback-prompt-text">
                      Please review what this session taught us.
                    </span>
                    <button
                      type="button"
                      className="d5-feedback-prompt-btn"
                      onClick={() => onOpenLearning()}
                    >
                      Review learning
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          }
          // Rendered only when a close followed a context-switch gate that left
          // a deferred question. The banner offers a one-click way to seed the
          // next session with that question instead of retyping. Empty-query rows
          // (no deferred question) are no longer created, but render null here as
          // a safety net so a stale or replayed row cannot resurface the bare
          // "This session is closed" text — the feedback_prompt row already
          // communicates closure.
          if (msg.type === "context_switch_resume") {
            const query = msg.query || "";
            if (!query) return null;
            const preview = query.length > 90 ? `${query.slice(0, 90).trim()}…` : query;
            return (
              <div key={msg.id || i} className="d5-resume-prompt-row">
                <span className="d5-resume-prompt-text">
                  Start a new session with: <em>&ldquo;{preview}&rdquo;</em>
                </span>
                <button
                  type="button"
                  className="d5-resume-prompt-btn"
                  onClick={() => onResumeContextSwitch?.(query)}
                >
                  Start New Session
                </button>
              </div>
            );
          }
          if (msg.type === "session_clear_divider") {
            return (
              <React.Fragment key={msg.id || i}>
                <div className="d5-clear-divider">
                  <span className="d5-clear-divider-title">Conversation cleared!</span>
                  <span className="d5-clear-divider-hint">
                    How can I help you today? Ask your next question and I'll start fresh.
                  </span>
                </div>
                {/* Dashboard2's spacer: pushes the old conversation off-screen so
                    the first answer of the new one starts on a clean view. */}
                <div className="d5-clear-spacer" aria-hidden="true" />
              </React.Fragment>
            );
          }
          if (msg.type === "case_analysis") {
            return (
              <div key={msg.id || i} className="d5-msg-block" data-msg-id={msg.id}>
                <div className="d5-msg d5-msg-copilot">
                  <div className="d5-msg-bubble d5-case-analysis-bubble">
                    <CaseAnalysisPanels analysis={msg.analysis} />
                  </div>
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {capName(copilotBotName)} {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          if (msg.type === "answered_batch") {
            // A DA batch card answered at once, laid out like an answered single
            // card: the questions and their options as the copilot's card on the
            // left, then your reply as one plain bubble on the right.
            return (
              <React.Fragment key={msg.id || i}>
                <div className="d5-msg-block d5-answered-block" data-msg-id={msg.id}>
                  <AnsweredBatchCard lines={msg.lines} />
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {capName(copilotBotName)} {formatTimestamp(msg.askedAt || msg.timestamp)}
                    </span>
                  </div>
                </div>
                <div className="d5-msg d5-msg-user d5-engineer-block" data-msg-id={msg.id}>
                  <div className="d5-msg-bubble d5-msg-bubble-user"><AnsweredBatchSummary lines={msg.lines} /></div>
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {capName(tseAgentName || localStorage.getItem("TseAgentName") || localStorage.getItem("username") || "Support Engineer")} {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          }
          if (msg.type === "answered_question") {
            const { question, options = [], pills = false } = msg.questionSnapshot || {};
            return (
              <React.Fragment key={msg.id || i}>
              <div className="d5-msg-block d5-answered-block" data-msg-id={msg.id}>
                <div className="d5-answered-card">
                  {question && (
                    <div
                      className="d5-answered-question"
                      dangerouslySetInnerHTML={{ __html: formatBotMessage(question) }}
                    />
                  )}
                  {options.length > 0 && (
                    <div className={pills ? "d5-answered-options d5-answered-options-pills" : "d5-answered-options"}>
                      {options.map((opt, oi) => (
                        <div
                          key={oi}
                          className={`d5-answered-option ${opt === msg.selectedAnswer ? "d5-answered-option-picked" : ""}`}
                          dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="d5-msg-meta">
                  <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                    {capName(copilotBotName)} {formatTimestamp(msg.askedAt || msg.timestamp)}
                  </span>
                </div>
              </div>
              {/* The answer as its own message on your side. The card above is
                  the copilot's question and the options it offered; this is what
                  you replied, so it reads like every other reply. */}
              {msg.selectedAnswer && (
                // Autoscroll anchors HERE, not on the block above: this is the
                // engineer's own line. data-msg-id is duplicated onto it (the
                // wrapper keeps its own for the citation-pill handler).
                <div className="d5-msg d5-msg-user d5-engineer-block" data-msg-id={msg.id}>
                  <div className="d5-msg-bubble d5-msg-bubble-user">{msg.selectedAnswer}</div>
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {capName(variant === "enterprise" ? (customerUsername || localStorage.getItem("username") || "User") : (tseAgentName || localStorage.getItem("TseAgentName") || localStorage.getItem("username") || "Support Engineer"))} {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                </div>
              )}
              </React.Fragment>
            );
          }
          // A human support agent, mid-escalation. Its own branch because the
          // two below cannot represent it: the copilot branch would attribute
          // the agent's words to the AI, and the user branch draws every
          // non-copilot sender as your own outgoing bubble — which is how the
          // agent's replies would have read as the reader's own messages.
          if (msg.sender === "live_agent") {
            return (
              <div key={msg.id || i} className="d5-msg-block" data-msg-id={msg.id}>
                <div className="d5-msg d5-msg-live-agent">
                  <div
                    className="d5-msg-bubble d5-msg-bubble-live-agent"
                    dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }}
                  />
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {capName(msg.agentName || tseAgentName || "Support Agent")} {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          const isCopilot = msg.sender === "copilot";
          // Marks the bubble below for autoscroll to anchor on — same element
          // as on a guided turn, so there is one rule for both (see
          // findEngineerBlock).
          const isEngineer = msg.sender === "user" || msg.sender === "agent";
          return (
            <div key={msg.id || i} className="d5-msg-block" data-msg-id={msg.id}>
              {isCopilot && (msg.sources?.length > 0) && (
                <SourceCards
                  sources={msg.sources}
                  isPreview={msg.sourcesPreview}
                  activeNumber={activeSourceByMsg[msg.id]}
                  citedNumbers={citedNumbersFor(msg)}
                  alternatives={msg.sourceAlternatives || []}
                  canReplace={!!msg.canReplaceSource && !sessionExpired && !sessionClosed}
                  replacingNumber={msg.replacingSourceNumber ?? null}
                  sourceAction={msg.sourceActionKind || null}
                  onCardClick={(src, number) => {
                    setActiveSourceByMsg((prev) => ({ ...prev, [msg.id]: number }));
                    openDocument(src?.citation || src?.url, onDocError);
                  }}
                  onOpenSourceMap={onOpenSourceMap}
                  onReplaceSource={(src, number, candidate) => {
                    onReplaceSource?.(msg, src, number, candidate);
                  }}
                  onCiteSource={(src, number) => {
                    onCiteSource?.(msg, src, number);
                  }}
                />
              )}
              {isCopilot && msg.daNarration && msg.isStreaming && !msg.text ? (
                // DA narration started (cards above) but no sentence yet: the turn's thinking row, in place.
                <div className="d5-status-row d5-narration-wait" role="status">
                  <div className="d5-typing-dots"><span /><span /><span /></div>
                  <span className="d5-status-text">{copilotLabel} is thinking…</span>
                </div>
              ) : isCopilot ? (
                  <StreamingMessage
                    text={msg.text}
                    sources={msg.sources}
                    isStreaming={msg.isStreaming}
                    isDone={msg.isDone !== false}
                    cacheHit={!!msg.cacheHit}
                    timestamp={msg.timestamp}
                    sender="copilot"
                    botName={copilotBotName}
                    onShare={variant === "enterprise" ? null : () => onShareToCustomer?.(msg.text || "")}
                    onEdit={variant === "enterprise" ? null : () => onEditInCustomer?.(msg.text || "")}
                  />
              ) : (
                <div
                  className={`d5-msg d5-msg-user${isEngineer ? " d5-engineer-block" : ""}`}
                  data-msg-id={msg.id}
                >
                  {msg.attachments?.length > 0 && (
                    <div className="d5-msg-attachments">
                      {msg.attachments.map((a) => (
                        a.kind === "image" ? (
                          <img
                            key={a.id}
                            src={a.previewUrl}
                            alt={a.name}
                            className="d5-msg-attachment-thumb"
                            onClick={() => setZoomImageUrl(a.previewUrl)}
                          />
                        ) : (
                          <span key={a.id} className="d5-msg-attachment-chip" title={a.name}>
                            <FileText size={12} />
                            {a.name}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                  {msg.text && <div className="d5-msg-bubble d5-msg-bubble-user">{msg.text}</div>}
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">
                      {variant === "enterprise"
                        ? `${capName(msg.senderName || customerUsername || localStorage.getItem("username") || "User")} ${formatTimestamp(msg.timestamp)}`
                        : `${capName(tseAgentName || msg.senderName || localStorage.getItem("TseAgentName") || localStorage.getItem("username") || "Support Engineer")} ${formatTimestamp(msg.timestamp)}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {(isStreaming || narrationPending) && !escalationActive && !messages.some((m) => m.isStreaming) && (
          <div className="d5-status-row">
            <div className="d5-typing-dots"><span /><span /><span /></div>
            <span className="d5-status-text">{copilotLabel} is thinking…</span>
          </div>
        )}
      </div>

      <div className="d5-composer">
        {/* Anchored to the composer so it floats directly above whatever it
            contains — question card open or collapsed. */}
        {showScrollDown && (
          <button type="button" className="d5-scroll-down" title="Jump to latest" onClick={scrollToBottom}>
            <ArrowDown size={15} />
          </button>
        )}
        {variant === "enterprise" && liveChatEnded && !liveChatNoticeDismissed && (
          <div className="d5-session-expired" role="status">
            <span className="d5-session-expired-text">Live chat ended — connection closed.</span>
            {/* Dismissible: this is a NOTICE, not a lock. It reports something
                that already happened and offers no action, so leaving it
                pinned above the composer just narrows the panel for the rest
                of the session. The expiry banner below is not dismissible on
                purpose — that one owns the only way forward. */}
            <button
              type="button"
              className="d5-session-expired-close"
              onClick={() => setLiveChatNoticeDismissed(true)}
              title="Dismiss"
              aria-label="Dismiss"
            >
              <X size={13} />
            </button>
          </div>
        )}
        {(sessionExpired || resumePending) && (
          <div className="d5-session-expired" role="alert">
            <span className="d5-session-expired-text">
              {/* A dropped socket is not a dead conversation — the server keeps
                  the session and replays it on rejoin, so the wording must not
                  declare it lost. A timeout usually IS terminal, so that copy
                  stays honest while still pointing at the button. */}
              {sessionEndReason === "disconnect"
                ? "Connection lost. Your conversation is still saved — try resuming it."
                : "Session timed out. Resuming may not bring it back; you can start a new one instead."}
            </span>
            <div className="d5-session-expired-actions">
              {onResumeSession && (
                <button
                  type="button"
                  className="d5-session-expired-action d5-session-expired-action-primary"
                  onClick={onResumeSession}
                  disabled={resumePending || reconnecting}
                >
                  {resumePending || reconnecting ? "Resuming…" : "Resume session"}
                </button>
              )}
              {onNewSession && (
                <button
                  type="button"
                  className="d5-session-expired-action d5-session-expired-action-secondary"
                  onClick={onNewSession}
                  disabled={resumePending}
                >
                  Start new session
                </button>
              )}
            </div>
          </div>
        )}
        {!sessionExpired && (copilotMode === "guided" || diagnosisActive) && (
          <div className="d5-guided-bar" role="group" aria-label="Step-by-step diagnostic mode">
            <span className="d5-guided-bar-dot" aria-hidden="true" />
            <span className="d5-guided-bar-label">Diagnostic mode</span>
            <span className="d5-guided-bar-hint">Step by step</span>
            <button
              type="button"
              className="d5-exit-guided"
              title="Leave diagnostic mode and use hybrid answers"
              onClick={onExitGuided}
            >
              <LogOut size={11} aria-hidden="true" />
              Exit diagnostic
            </button>
          </div>
        )}
        {/* Followups render inline in the transcript (see the "followups"
            branch above), so the composer card is suppressed for them.
            Diagnostic MCQs and the guided-session offer still use it. */}
        {!isEmpty && currentQuestion && currentQuestion.question !== FOLLOWUPS_MARKER && !sessionExpired && (
          <>
            <QuestionCard
              question={currentQuestion.question}
              options={currentQuestion.options}
              inputType={currentQuestion.input_type || currentQuestion.inputType}
              pills={Boolean(currentQuestion.diagnosis_id)}
              questions={currentQuestion.questions || null}
              batchState={liveBatch ? batchPicks : null}
              typedOverride={Boolean(liveBatch) && hasDraftText && batchPicks.answered > 0}
              onAnswer={handleAnswer}
              onDismiss={onDismissQuestion}
              disabled={isStreaming}
            />
          </>
        )}
        {attachError && <div className="d5-attachment-error">{attachError}</div>}
        {/* One bordered box for attachments + input, not two -- it grows
            upward as chips are added instead of stacking a separate card
            above a separate input row. */}
        <div className="d5-composer-box">
          {attachments.length > 0 && (
            <div className="d5-composer-attachments">
              {attachments.map((a) => (
                <div key={a.id} className={`d5-attachment-chip${a.kind === "image" ? " d5-attachment-chip-image" : ""}`}>
                  {a.kind === "image" ? (
                    <img src={a.previewUrl} alt={a.name} className="d5-attachment-thumb" />
                  ) : (
                    <span className="d5-attachment-icon"><FileText size={14} /></span>
                  )}
                  <span className="d5-attachment-info">
                    <span className="d5-attachment-name" title={a.name}>{a.name}</span>
                    <span className="d5-attachment-size">
                      {formatBytes(a.size)}
                      {isBetaExt(extOf(a.name)) && (
                        <span className="d5-attach-beta-tag" title="Experimental format — check what the copilot read back from it">
                          Beta
                        </span>
                      )}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="d5-attachment-remove"
                    title={`Remove ${a.name}`}
                    onClick={() => removeAttachment(a.id)}
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="d5-composer-row">
            <input
              ref={imageInputRef}
              type="file"
              multiple
              accept={ACCEPT_IMAGES}
              style={{ display: "none" }}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={documentInputRef}
              type="file"
              multiple
              accept={ACCEPT_DOCUMENTS}
              style={{ display: "none" }}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = "";
              }}
            />
            <div className="d5-attach-wrap" ref={attachMenuWrapRef}>
              <button
                type="button"
                className="d5-composer-attach"
                title={`Attach images or files (${SUPPORTED_FORMATS_LABEL})`}
                disabled={isStreaming || sessionExpired || sessionClosed || reconnecting || hydrating || attachments.length >= MAX_ATTACHMENTS}
                aria-expanded={attachMenuOpen}
                aria-haspopup="true"
                onClick={() => setAttachMenuOpen((v) => !v)}
              >
                <Plus size={16} />
              </button>
              <div className={`d5-attach-menu${attachMenuOpen ? " d5-attach-menu--open" : ""}`}>
                <button
                  type="button"
                  className="d5-attach-menu-item"
                  onClick={() => {
                    setAttachMenuOpen(false);
                    imageInputRef.current?.click();
                  }}
                >
                  <span className="d5-attach-menu-icon"><ImageIcon size={15} /></span>
                  <span className="d5-attach-menu-text">
                    <span className="d5-attach-menu-title">Add images</span>
                    <span className="d5-attach-menu-subtitle">{IMAGE_FORMATS_LABEL}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="d5-attach-menu-item"
                  onClick={() => {
                    setAttachMenuOpen(false);
                    documentInputRef.current?.click();
                  }}
                >
                  <span className="d5-attach-menu-icon"><Paperclip size={15} /></span>
                  <span className="d5-attach-menu-text">
                    <span className="d5-attach-menu-title">Add files</span>
                    <span className="d5-attach-menu-subtitle">{DOCUMENT_STABLE_LABEL}</span>
                    {/* Its OWN line, with the tag LEADING the group. Trailing the
                        tag after one long comma list read as "all of these are
                        beta", which is the opposite of what it means. */}
                    {DOCUMENT_BETA_LABEL && (
                      <span className="d5-attach-menu-subtitle d5-attach-menu-subtitle-beta">
                        <span className="d5-attach-beta-tag">Beta</span>
                        {DOCUMENT_BETA_LABEL}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            </div>
            {/* The closed branch sits second, ahead of liveChatEnded/reconnecting/
                hydrating, because a closed case is terminal and those are not:
                the box is dead for the rest of this session either way, so the
                placeholder must say WHY rather than "Reconnecting…" or
                "Loading conversation…". It is the only feedback the input can
                give — the textarea is `disabled`, so there is no typing, no
                keydown and no click to react to. Covers every way a session
                reaches that state: the close intent mid-chat, the
                context-switch gate, and a chat reopened from Recents that was
                already closed (TseDashboard5 sets sessionClosed off the join
                ack, before hydration finishes). Wording is variant-split
                because the rail button the customer is being pointed at says
                "New chat", while the engineer's copy elsewhere in this panel
                (attach, action bar) says "start a new session". */}
            <textarea
              className="d5-composer-input"
              placeholder={sessionExpired ? (sessionEndReason === "disconnect" ? "Connection closed — start a new session to continue." : "Session timed out — start a new session to continue.") : sessionClosed ? (variant === "enterprise" ? "This chat is closed — start a new chat to continue." : "Session closed — start a new session to continue.") : (variant === "enterprise" && liveChatEnded) ? "Live chat ended — connection closed." : reconnecting ? "Reconnecting… your session is still here." : hydrating ? "Loading conversation…" : isEmpty ? "What issue are you troubleshooting?" : isStreaming ? "Copilot is responding…" : currentQuestion?.question === FOLLOWUPS_MARKER ? "Pick a suggestion above or type…" : liveBatch ? (batchPicks.complete ? BATCH_READY_PLACEHOLDER : BATCH_PENDING_PLACEHOLDER) : currentQuestion ? "Select an option above or type answer..." : "Ask follow-up, or use actions above..."}
              value={draft}
              disabled={isStreaming || sessionExpired || sessionClosed || reconnecting || hydrating || (variant === "enterprise" && liveChatEnded)}
              ref={textareaRef}
              rows={1}
              style={{ minHeight: COMPOSER_MIN_HEIGHT, maxHeight: `${COMPOSER_MAX_HEIGHT}px` }}
              onChange={(e) => {
                setDraft(e.target.value);
                autoResize(e.target);
              }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            {isStreaming ? (
              <button type="button" className="d5-composer-stop" title="Stop" onClick={onStop}>
                <Square size={10} fill="currentColor" strokeWidth={0} />
              </button>
            ) : (
              /* Not greyed out for an empty draft. A disabled send reads as "the
                 copilot is broken" far more often than "type something first" —
                 the placeholder already says what to do, and handleSend keeps the
                 real guard. Expired/closed/reconnecting DO disable it: there the
                 click genuinely cannot go anywhere. */
              <button
                type="button"
                className="d5-composer-send"
                disabled={sessionExpired || sessionClosed || reconnecting || hydrating || (variant === "enterprise" && liveChatEnded) || (batchAwaiting && !batchPicks.complete)}
                onClick={handleSend}
                /* A disabled button swallows the click silently, so the tooltip
                   carries the reason — same sentence as the placeholder, since
                   a greyed-out arrow next to a greyed-out box is otherwise two
                   dead controls with one explanation between them. */
                title={sessionClosed ? (variant === "enterprise" ? "This chat is closed — start a new chat to continue." : "Session closed — start a new session to continue.") : batchAwaiting ? (batchPicks.complete ? BATCH_SEND_LABEL : BATCH_PENDING_PLACEHOLDER) : "Send"}
                aria-label={batchAwaiting && batchPicks.complete ? BATCH_SEND_LABEL : undefined}
              >
                <Send size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
      <ResultsPanel
        open={!!resultsTab}
        activeTab={resultsTab || "bugs"}
        bugs={bugs}
        cases={cases}
        meta={{
          bugs: {
            searched: !!resultsMeta.bugs?.searched,
            lowRelevance: !!resultsMeta.bugs?.lowRelevance,
            sectionText: resultsMeta.bugs?.sectionText || "",
          },
          cases: {
            searched: !!resultsMeta.cases?.searched,
            lowRelevance: !!resultsMeta.cases?.lowRelevance,
            sectionText: resultsMeta.cases?.sectionText || "",
          },
        }}
        onTabChange={openResults}
        onClose={() => setResultsTab(null)}
      />
      {/* Took ResultsPanel's place as the context host, so it inherits the
          reset wire too: ctxResetKey is what wipes the panel on "New chat".
          See SessionContextDrawer. */}
      <SessionContextDrawer
        open={contextOpen}
        sessionId={sessionId}
        ctxTurnKey={ctxTurnKey}
        ctxResetKey={ctxResetKey}
        onClose={() => setContextOpen(false)}
      />
      {zoomImageUrl && <ImagePreviewModal imageUrl={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />}
      {showEndChatConfirm && (
        <EndChatConfirmModal
          title="End live chat?"
          description="Are you sure you want to end the live chat? You'll go back to the AI assistant."
          onCancel={() => setShowEndChatConfirm(false)}
          onConfirm={() => {
            setShowEndChatConfirm(false);
            onEndLiveChat?.();
          }}
        />
      )}
    </div>
  );
});

CopilotPanel.displayName = "CopilotPanel";

export default CopilotPanel;
